import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { Mail, Gift, Check, Loader2 } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const SEOCourseSection = () => {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const days = Array.from({ length: 7 }, (_, i) => ({
    n: i + 1,
    title: t[`course_d${i + 1}_t`] || "",
    subtitle: t[`course_d${i + 1}_s`] || "",
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError(t.course_err_invalid || "Please enter a valid email");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/courses/seo-7day/subscribe`, {
        email,
        language: language || "en",
        source: "landing",
      });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || t.course_err_invalid || "Subscription failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="seo-course"
      data-testid="seo-course-section"
      className="relative py-24 themed-section"
      style={{ background: "var(--color-bg)" }}
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <div
          className="rounded-2xl overflow-hidden border grid lg:grid-cols-2"
          style={{
            background: "var(--color-surface)",
            borderColor: "var(--color-border)",
          }}
        >
          {/* Left — Pitch */}
          <div className="p-8 lg:p-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full"
              style={{
                background: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
                color: "var(--color-primary)",
              }}
            >
              <Gift className="w-3.5 h-3.5" />
              <span className="text-[10px] font-mono uppercase tracking-widest font-bold">
                {t.course_tag || "Free · No credit card"}
              </span>
            </div>

            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-4"
              style={{ color: "var(--color-text)" }}
            >
              {t.course_title_a || "7 days to"}{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
                }}
              >
                {t.course_title_b || "#1 position"}
              </span>
            </h2>

            <p
              className="mb-6"
              style={{ color: "var(--color-text-muted)" }}
            >
              {t.course_subtitle || "Free email course: 7 practical lessons, 7 concrete tasks, 1 BONUS discount code at the end."}
            </p>

            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" style={{ color: "var(--color-accent)" }} />
                <span style={{ color: "var(--color-text)" }}>{t.course_feature_1 || "7 emails × 5 min reading"}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4" style={{ color: "var(--color-accent)" }} />
                <span style={{ color: "var(--color-text)" }}>{t.course_feature_2 || "Copy-paste actionable tasks"}</span>
              </li>
              <li className="flex items-center gap-2">
                <Gift className="w-4 h-4" style={{ color: "var(--color-accent)" }} />
                <span style={{ color: "var(--color-text)" }}>
                  <span className="font-bold">{t.course_feature_bonus || "BONUS:"}</span> {t.course_feature_3 || "20% discount code on completion"}
                </span>
              </li>
            </ul>

            {result ? (
              <div
                data-testid="course-success"
                className="p-4 rounded-lg border"
                style={{
                  background: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
                  borderColor: "var(--color-primary)",
                }}
              >
                <p className="font-bold mb-2" style={{ color: "var(--color-text)" }}>
                  {t.course_success || "✅ Done! Check your email."}
                </p>
                {result.discount_code && (
                  <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                    {t.course_code_label || "Your BONUS code:"}{" "}
                    <code
                      className="font-mono px-2 py-1 rounded"
                      style={{
                        background: "var(--color-surface-alt)",
                        color: "var(--color-primary)",
                      }}
                    >
                      {result.discount_code}
                    </code>
                  </p>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.course_email_placeholder || "your@email.com"}
                    required
                    data-testid="course-email-input"
                    className="flex-1 px-4 py-3 rounded-lg border text-sm focus:outline-none"
                    style={{
                      background: "var(--color-surface-alt)",
                      borderColor: "var(--color-border)",
                      color: "var(--color-text)",
                    }}
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="course-submit-btn"
                    className="px-6 py-3 font-bold rounded-lg transition-transform hover:scale-105 flex items-center gap-2"
                    style={{
                      background: "var(--color-primary)",
                      color: "var(--color-bg)",
                    }}
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (t.course_submit || "Send →")}
                  </button>
                </div>
                {error && (
                  <p className="text-xs" style={{ color: "#ef4444" }}>
                    {error}
                  </p>
                )}
                <p
                  className="text-[11px]"
                  style={{ color: "var(--color-text-muted)" }}
                >
                  {t.course_privacy || "🔒 No spam · Unsubscribe 1 click · GDPR-friendly"}
                </p>
              </form>
            )}
          </div>

          {/* Right — 7-day schedule */}
          <div
            className="p-8 lg:p-10"
            style={{
              background: "var(--color-surface-alt)",
              borderLeft: "1px solid var(--color-border)",
            }}
          >
            <p
              className="text-[10px] font-mono uppercase tracking-widest mb-5"
              style={{ color: "var(--color-text-muted)" }}
            >
              {t.course_program || "7-day program"}
            </p>
            <div className="space-y-2.5">
              {days.map((d) => (
                <div
                  key={d.n}
                  data-testid={`course-day-${d.n}`}
                  className="flex items-start gap-3 p-2.5 rounded-lg transition-colors hover:opacity-80"
                  style={{ background: "var(--color-surface)" }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center font-black shrink-0 text-sm"
                    style={{
                      background: "var(--color-primary)",
                      color: "var(--color-bg)",
                    }}
                  >
                    D{d.n}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-bold text-sm"
                      style={{ color: "var(--color-text)" }}
                    >
                      {d.title}
                    </p>
                    <p
                      className="text-xs"
                      style={{ color: "var(--color-text-muted)" }}
                    >
                      {d.subtitle}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SEOCourseSection;
